'use client'

import { useActionState, useEffect, useState, useTransition } from 'react'

import {
  Button,
  Divider,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { Building2 } from 'lucide-react'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { z } from 'zod'

import { routes, urls } from '@/constants/routes'
import { LOGIN_INITIAL_STATE } from '@/lib/api/auth/auth.constants'
import { login } from '@/lib/api/auth/auth.service'
import type { LoginFormData, LoginFormState } from '@/lib/api/auth/auth.types'
import { RETURN_URL_PARAM, readReturnUrl, rememberReturnUrl } from '@/lib/auth/return-url'

import CardFormAlert from '../components/form-error-message'

const LoginForm = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false)
  const router = useRouter()

  const tAuth = useTranslations('auth')
  const tCommon = useTranslations('common')

  const schema = z.object({
    username: z
      .email({ error: tAuth('login.form.fields.email.error.invalid') })
      .min(1, { error: tAuth('login.form.fields.email.error.required') }),
    password: z
      .string()
      .min(1, { error: tAuth('login.form.fields.password.error.required') }),
  })

  const form = useForm<LoginFormData>({
    mode: 'uncontrolled',
    initialValues: {
      username: '',
      password: '',
    },
    validate: zod4Resolver(schema),
  })

  const [, startTransition] = useTransition()

  const [state, submitAction, isSubmitting] = useActionState<
    LoginFormState,
    LoginFormData
  >(login, LOGIN_INITIAL_STATE)
  const isPending = isSubmitting || state.success

  const handleSubmit = (values: LoginFormData) => {
    startTransition(() => {
      submitAction(values)
    })
  }

  useEffect(() => {
    const returnPath = new URLSearchParams(globalThis.location.search).get(
      RETURN_URL_PARAM,
    )
    rememberReturnUrl(returnPath)
  }, [])

  useEffect(() => {
    if (state.success && state.nextStep === 'updatePassword') {
      router.push(routes.updatePassword)
    }
    if (state.success && state.nextStep === 'done') {
      globalThis.location.href = readReturnUrl()
    }
  }, [state.success, state.nextStep, router])

  return (
    <form onSubmit={form.onSubmit(handleSubmit)} noValidate style={{ width: '100%' }}>
      <Stack gap='2xl'>
        <Stack gap='xl'>
          <Title size='h2' fz={24} opacity={isPending ? '0.2' : 1} ta='center'>
            {tAuth('login.title')}
          </Title>
          <Stack gap='lg'>
            <Stack
              gap='md'
              opacity={isPending ? 0.2 : 1}
              style={{
                pointerEvents: isPending ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-out',
              }}
            >
              <TextInput
                error={form.errors.username}
                label={tAuth('login.form.fields.email.label')}
                placeholder={tAuth('login.form.fields.email.placeholder')}
                type='email'
                size='xs'
                required
                {...form.getInputProps('username')}
              />
              <PasswordInput
                error={form.errors.password}
                label={tAuth('login.form.fields.password.label')}
                onVisibilityChange={setIsPasswordVisible}
                placeholder={tAuth('login.form.fields.password.placeholder')}
                visible={isPasswordVisible}
                size='xs'
                required
                {...form.getInputProps('password')}
              />
            </Stack>

            <Button
              autoContrast
              fullWidth
              type='submit'
              loading={isPending}
              loaderProps={{ type: 'dots' }}
              size='xs'
            >
              {tAuth('login.form.submit.label')}
            </Button>
          </Stack>

          <CardFormAlert message={state.errorMessage} />

          <Group
            justify='flex-end'
            fz='xs'
            opacity={isPending ? 0.2 : 1}
            style={{
              pointerEvents: isPending ? 'none' : 'auto',
              transition: 'opacity 0.5s ease-out',
            }}
          >
            <Link
              href={routes.forgotPassword}
              style={{
                color: 'var(--mantine-color-text-dimmed)',
                textDecoration: 'none',
              }}
            >
              {tAuth('login.forgot-password')}
            </Link>
          </Group>
        </Stack>

        <Divider
          color='var(--mantine-color-border-surface)'
          label={tCommon('or')}
          labelPosition='center'
          opacity={isPending ? 0.2 : 1}
          style={{
            pointerEvents: isPending ? 'none' : 'auto',
            transition: 'opacity 0.5s ease-out',
          }}
        />
        <Link
          href={urls.account}
          style={{
            textAlign: 'center',
            textDecoration: 'none',
            opacity: isPending ? 0.2 : 1,
            pointerEvents: isPending ? 'none' : 'auto',
            transition: 'opacity 0.5s ease-out',
          }}
        >
          <Group justify='center' gap='sm'>
            <Building2
              color='var(--mantine-color-text-dimmed)'
              size={16}
              strokeWidth={1}
            />
            <Text c='var(--mantine-color-text-dimmed)' size='xs'>
              {tAuth('login.change-account')}
            </Text>
          </Group>
        </Link>
      </Stack>
    </form>
  )
}

export default LoginForm
